import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronDown, Layers, Globe, Building2, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { homeContent, subsidiaries, leadershipTeam } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import AnimatedSection from '../components/ui/AnimatedSection';
import SubsidiaryIcon from '../components/ui/SubsidiaryIcon';
import StatsSection from '../components/sections/StatsSection';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import NewsletterSection from '../components/sections/NewsletterSection';
import VideoSection from '../components/sections/VideoSection';

const HomePage = () => {
  const { language, t, pick } = useLanguage();
  useDocumentTitle(language === 'fr' ? 'Accueil' : 'Home');
  const c = homeContent;
  const sectionEyebrows = {
    en: { wgs: 'A Next-Generation Holding Company', eight: 'Eight Specialized Companies', approach: 'Our Approach', leadership: 'Our Chairman' },
    fr: { wgs: 'Une holding de nouvelle génération', eight: 'Huit entreprises spécialisées', approach: 'Notre approche', leadership: 'Notre Président' }
  }[language];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 30% 50%, rgba(91,122,138,0.18) 0%, transparent 50%)'
        }} />

        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 border border-[#5B7A8A] rounded-full"></div>
          <div className="absolute bottom-40 left-10 w-64 h-64 border border-[#5B7A8A] rounded-full"></div>
        </div>

        <div className="container-swg relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-8">
              <span className="caption">{t('splash.hint')}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="brand-display text-white mb-8">
              {pick(c.hero.title)}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-xl md:text-2xl text-[#9AACB8] font-medium mb-12 max-w-2xl">
              {pick(c.hero.subtitle)}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="flex flex-wrap gap-4">
              <Link to="/ecosystem" className="btn-primary">
                {t('cta.exploreEcosystem')} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/partnerships" className="btn-secondary">{t('cta.partnerWithUs')}</Link>
            </motion.div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="scroll-indicator">
          <span className="text-xs uppercase tracking-widest">{language === 'fr' ? 'Défiler' : 'Scroll'}</span>
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </section>

      <StatsSection />

      {/* Introduction */}
      <section className="section-padding-lg bg-[#1a1c1b] border-t border-[rgba(91,122,138,0.2)]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <span className="caption text-[#5B7A8A] block mb-4">{pick(c.introduction.title)}</span>
              <h2 className="heading-2 text-white mb-6">{sectionEyebrows.wgs}</h2>
              <div className="accent-line mb-8"></div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <p className="body-large mb-6">{pick(c.introduction.text)}</p>
              <p className="body-medium text-[#5B7A8A]">{pick(c.introduction.vision)}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#5B7A8A]/10">
        <div className="container-swg">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center">
              <span className="caption text-[#5B7A8A] block mb-6">{t('home.ourMission')}</span>
              <p className="text-xl md:text-2xl text-white leading-relaxed">"{pick(c.mission)}"</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <VideoSection />

      {/* Ecosystem Overview */}
      <section className="section-padding-lg bg-[#252728]">
        <div className="container-swg">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="caption text-[#5B7A8A] block mb-4">{pick(c.ecosystem.title)}</span>
              <h2 className="heading-2 text-white mb-6">{sectionEyebrows.eight}</h2>
              <p className="body-large">{pick(c.ecosystem.text)}</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {subsidiaries.slice(0, 4).map((sub, index) => (
              <AnimatedSection key={sub.id} delay={index * 0.04}>
                <Link
                  to={`/subsidiary/${sub.id}`}
                  className="group relative flex flex-col items-center justify-center aspect-square rounded-2xl bg-[#1a1c1b]/60 border border-[rgba(91,122,138,0.18)] p-6 transition-all duration-300 hover:-translate-y-1"
                  style={{ ['--brand']: sub.color }}
                  data-testid={`home-subsidiary-${sub.id}`}
                >
                  {/* Glow on hover */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${sub.color}26 0%, transparent 70%)`,
                      boxShadow: `0 12px 40px ${sub.color}33, inset 0 0 0 1px ${sub.color}55`,
                    }}
                  />
                  <div className="relative transition-transform duration-300 group-hover:scale-110">
                    <SubsidiaryIcon subsidiary={sub} size="lg" />
                  </div>
                  <h3 className="relative mt-5 text-white font-semibold text-base md:text-lg text-center">
                    {sub.name}
                  </h3>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={0.4}>
            <div className="text-center mt-12">
              <Link to="/ecosystem" className="btn-secondary">
                {t('cta.viewAll')} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Smartworld */}
      <section className="section-padding-lg bg-[#1a1c1b]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <span className="caption text-[#5B7A8A] block mb-4">{sectionEyebrows.approach}</span>
              <h2 className="heading-2 text-white mb-6">{pick(c.whyUs.title)}</h2>
              <div className="accent-line mb-8"></div>
              <p className="body-large">{pick(c.introduction.vision)}</p>
            </AnimatedSection>

            <div className="space-y-6">
              {pick(c.whyUs.points).map((point, index) => {
                const icons = [Layers, Building2, Globe, TrendingUp];
                const Icon = icons[index];
                return (
                  <AnimatedSection key={index} delay={index * 0.1}>
                    <div className="flex items-start gap-4 p-5 swg-card rounded-xl">
                      <div className="w-12 h-12 bg-[#3d5a6a]/50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#5B7A8A]" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{point}</h4>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section-padding-lg bg-[#252728]">
        <div className="container-swg">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="caption text-[#5B7A8A] block mb-4">{t('home.leadership')}</span>
              <h2 className="heading-2 text-white mb-6">{sectionEyebrows.leadership}</h2>
              <div className="accent-line mx-auto"></div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="max-w-4xl mx-auto">
              <div className="swg-card rounded-2xl overflow-hidden flex flex-col md:flex-row">
                <div className="md:w-1/3">
                  <img src={leadershipTeam[0].image} alt={leadershipTeam[0].name} className="w-full h-full object-cover min-h-[300px]" />
                </div>
                <div className="md:w-2/3 p-8 md:p-10 flex flex-col justify-center">
                  <h3 className="text-white font-bold text-2xl mb-2">{leadershipTeam[0].name}</h3>
                  <p className="text-[#5B7A8A] font-medium mb-6">{pick(leadershipTeam[0].title)}</p>
                  <p className="text-[#9AACB8] leading-relaxed">{pick(leadershipTeam[0].bio)}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <TestimonialsSection />

      {/* CTA */}
      <section className="section-padding bg-[#3d5a6a]/20 border-y border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <AnimatedSection>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="heading-3 text-white mb-2">{t('cta.readyToBuild')}</h2>
                <p className="text-[#9AACB8]">{t('cta.readyToBuildSub')}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/partnerships" className="btn-primary">{t('cta.partnerWithUs')}</Link>
                <Link to="/contact" className="btn-secondary">{t('cta.contactUs')}</Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <NewsletterSection />
    </div>
  );
};

export default HomePage;
